import {
  addMonths,
  parse,
  startOfToday,
  subDays,
  subMilliseconds,
} from "date-fns";
import { getTimezoneOffset } from "date-fns-tz";
import { Frequency, WeekdayStr, RRuleSet, RRule } from "rrule";
import { datef, fromLocalTimezone, TimeFormatPreference, timeLabel, toLocalTimezone } from "../dates";
import { CalendarBlockFilters, CalendarBlockType, defaultCalendarBlockTitles, getDefaultFilters } from "../calendarBlockHelper";
import { Addressable } from "../addresses";
import { isEqual, pick } from "lodash";

// All Dates are in *local timezone* for ease of display and editing.
// If you are making any changes to the shape of these RRule types,
// consider if `findAvailSimilarTo` needs to be updated as well.
export type DailyRRule = {
  freq: Frequency.DAILY,
  dtstart: Date,
  until: Date,
};

export type WeeklyRRule = {
  freq: Frequency.WEEKLY,
  dtstart: Date,
  until?: Date,
  interval: number,
  byweekday: WeekdayStr[],
};

export type ProviderTravel = "either" | "inHomeOnly" | "virtualOnly";

export type AvailabilityFromGQL = {
  id: number;
  blockType: CalendarBlockType,
  blockFilters: CalendarBlockFilters | null,
  endTime: string;
  note:string;
  providerId:number;
  providerTravel: ProviderTravel;
  rruleByweekday?: string | (string | null)[] | null;
  rruleDtstart: string;
  rruleFreq: 'daily' | 'weekly';
  rruleInterval?: number | null;
  rruleUntil?: string | null;
  shiftCenterId?: number | null;
  overflowShift?: boolean | null;
  startTime: string;
  type: 'available' | 'unavailable' | 'calendar_block';
  useShiftCenterDriveTimes?: boolean | null;
} & Addressable

export type Availability = {
  id: number;
  type: 'available' | 'unavailable' | 'calendar_block';
  localStartTime: Date;
  localEndTime: Date;
  note: string;
  providerId: number;

  providerTravel: ProviderTravel;
  shiftCenterId: number | null;
  rrule: DailyRRule | WeeklyRRule;
  overflowShift: boolean;
  useShiftCenterDriveTimes: boolean;

  blockType: CalendarBlockType,
  blockFilters: CalendarBlockFilters | null,

  _rrule: RRule,
} & Addressable;

export const daysOfWeek: WeekdayStr[] =  ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;
const daysSet = new Set(daysOfWeek);

/*
 * Takes an array - possibly including "null" or similar - and ensures that
 * we get back a clean one on only WeekdayStr.
 *
 * The default value is "eveny day of the week" - because this is how rrule interprets an
 * empty array, and we'd prefer to be explicit and display this in the UI.
 * The UI prevents saving recurring rules with no days set to avoid this issue,
 * but this at least gives us a more human-legible fallback.
 */
const filterDaysArray = (input: any): WeekdayStr[] => {
  if (!input) { return daysOfWeek; }

  const filtered = input.filter((d: any) => daysSet.has(d))
  if (!filtered.length) { return daysOfWeek; }

  return filtered;
}

// Takes an Availability entry in the database, and converts it to a convenient format
// for use in the UI. All Dates in Availabiliy represent dates (start of a day) or times of day in the *device's local timezone*
export const parseAvailability = (dbAvail: AvailabilityFromGQL): Availability => {
  const startTime = fromLocalTimezone(parse(dbAvail.startTime, "HH:mm:ss", new Date()));
  const endTime = fromLocalTimezone(parse(dbAvail.endTime, "HH:mm:ss", new Date()));

  const rrule = dbAvail.rruleFreq === 'daily' ? {
    freq: Frequency.DAILY,
    dtstart: toLocalTimezone(new Date(dbAvail.rruleDtstart)),
    until: dbAvail.rruleUntil && toLocalTimezone(new Date(dbAvail.rruleUntil)),
  } as DailyRRule : {
    freq: Frequency.WEEKLY,
    dtstart: toLocalTimezone(new Date(dbAvail.rruleDtstart)),
    until: dbAvail.rruleUntil ? toLocalTimezone(new Date(dbAvail.rruleUntil)) : undefined,
    interval: dbAvail.rruleInterval || 1,
    byweekday: filterDaysArray(dbAvail.rruleByweekday)
  } as WeeklyRRule;

  return {
    id: dbAvail.id,
    providerId: dbAvail.providerId,
    type: dbAvail.type,
    note: dbAvail.note,
    localStartTime: toLocalTimezone(startTime),
    localEndTime: toLocalTimezone(endTime),
    providerTravel: dbAvail.providerTravel || null,
    shiftCenterId: dbAvail.shiftCenterId || null,
    overflowShift: dbAvail.overflowShift || false,
    useShiftCenterDriveTimes: dbAvail.useShiftCenterDriveTimes || false,
    blockType: dbAvail.blockType,
    blockFilters: dbAvail.blockFilters || null,
    addressStreet: dbAvail.addressStreet,
    addressCity: dbAvail.addressCity,
    addressState: dbAvail.addressState,
    addressZip: dbAvail.addressZip,
    rrule,
    _rrule: new RRule(rrule),
  }
}

// Takes an Availability in the device's local timezone and converts all dates and times of day to UTC for storage
// in the database
export const unparseAvailability = <TypeEnum = Availability["type"], TravelEnum = Availability["providerTravel"], BlockTypeEnum = Availability["blockType"], FreqEnum = Availability["rrule"]["freq"]>(avail: Availability, providerId: number) => {
  const byweekday = avail.rrule.freq === Frequency.WEEKLY ? filterDaysArray(avail.rrule.byweekday) : [];

  const blockFilters: CalendarBlockFilters = avail.blockFilters ? {
      visitTypeIds: avail.blockFilters.visitTypeIds,
    } : null;

  return {
    id: avail.id,
    type: avail.type as TypeEnum,
    providerId,
    note: avail.note,
    startTime: datef(avail.localStartTime, "HH:mm:ss"),
    endTime: datef(avail.localEndTime, "HH:mm:ss"),

    providerTravel: avail.providerTravel as TravelEnum,
    shiftCenterId: avail.shiftCenterId,
    overflowShift: avail.overflowShift,
    useShiftCenterDriveTimes: avail.useShiftCenterDriveTimes,

    blockType: avail.blockType as BlockTypeEnum,
    blockFilters,
    addressStreet: avail.addressStreet,
    addressCity: avail.addressCity,
    addressState: avail.addressState,
    addressZip: avail.addressZip,

    rruleFreq: (avail.rrule.freq === Frequency.DAILY ?'daily' : 'weekly') as FreqEnum,
    rruleDtstart: fromLocalTimezone(avail.rrule.dtstart).toUTCString(),
    rruleInterval: (avail.rrule as any).interval,
    rruleByweekday: JSON.stringify(byweekday),
    rruleUntil: avail.rrule.until ? fromLocalTimezone(avail.rrule.until).toUTCString() : null,
  }
}

export const calculateAvailableDays = (month: Date, availability: Availability[]) => {
  const rulesets = {
    available: new RRuleSet(),
    unavailable: new RRuleSet(),
  }

  availability.forEach(({ type: availType, _rrule }) => {
    if (availType !== "calendar_block") {
      rulesets[availType].rrule(_rrule);
    }
  });

  const start = subDays(month, 1);
  const end = addMonths(month, 1);

  return {
    // The dates rrule gives us back are in UTC, convert them to the local timezone
    available: rulesets.available.between(start, end).map(toLocalTimezone),
    unavailable: rulesets.unavailable.between(start, end).map(toLocalTimezone),
  }
}

const weeklyInterval: Record<number, string | undefined> = {
  1: "Weekly",
  2: "Every other week",
}

export const freqToString = (freq: Frequency, interval: number = 1) => freq === RRule.DAILY ? "Once" : weeklyInterval[interval] ?? `Every ${interval} weeks`;

export const defaultWorkday = { localStartTime: new Date(), localEndTime: new Date() };
defaultWorkday.localStartTime.setHours(9, 0, 0, 0);
defaultWorkday.localEndTime.setHours(17, 0, 0, 0);

export const allDay = { localStartTime: new Date(), localEndTime: new Date() };
allDay.localStartTime.setHours(0, 0, 0, 0);
allDay.localEndTime.setHours(23, 59, 0, 0);

const defaultBreak = { localStartTime: new Date(), localEndTime: new Date() };
defaultBreak.localStartTime.setHours(12, 0, 0, 0);
defaultBreak.localEndTime.setHours(13, 0, 0, 0);

export const isAllDay = (a: Availability) => {
  return datef(a.localStartTime, "HH:mm") === "00:00" && datef(a.localEndTime, "HH:mm") === "23:59"
};
export const isDefaultWorkday = (a: Availability) => {
  return datef(a.localStartTime, "HH:mm") === "09:00" && datef(a.localEndTime, "HH:mm") === "17:00";
}

// Helper to pull "unavailable" items to the top, since they override "available" entries.
const sortOrder = { unavailable: 0, available: 1, calendar_block: 2 }
export const sortByType = (a: Availability, b: Availability) => sortOrder[a.type] - sortOrder[b.type]

const DAYS = {MO: 'Mon', TU: 'Tue', WE: 'Wed', TH: 'Thu', FR: 'Fri', SA: 'Sat', SU: 'Sun'}
export const strJoinByWeekday = (avail: Availability) => {
  if (avail.rrule.freq === RRule.DAILY || avail.rrule.byweekday.length === 0) { return ""; }
  if (avail.rrule.byweekday.length === 7) { return "All days"; }

  const array = avail.rrule.byweekday.map(d => DAYS[d]);
  if (array.length === 1) { return array[0] }

  if (array.length === 5 && array.indexOf('Sat') === -1 && array.indexOf('Sun') === -1) { return "Weekdays"; }
  if (array.length === 6) { return "Daily except " + Object.values(DAYS).find(d => array.indexOf(d) === -1); }
  return array.slice(0, -1).join(', ') + ' and ' + array[array.length - 1];
}

const DAYS_SHORT = {MO: 'M', TU: 'T', WE: 'W', TH: 'Th', FR: 'F', SA: 'S', SU: 'S'}
export const shortByWeekday = (avail: Availability) => {
  if (avail.rrule.freq === RRule.DAILY || avail.rrule.byweekday.length === 0) { return "Once"; }
  
  if (avail.rrule.until) {
    const daysDiff = Math.ceil((avail.rrule.until.getTime() - avail.rrule.dtstart.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff <= 1) {
      return "Once";
    }
  }
  
  if (avail.rrule.byweekday.length === 7) { return "Daily"; }

  let str = "";
  const days = [...avail.rrule.byweekday];

  // In our UI we treat Sunday as the first day, while rrule treats it as the last.
  if (days[days.length - 1] === "SU") {
    days.unshift(days.pop()!);
  }

  while (days.length) {
    const day = days.shift()!;
    const num = daysOfWeek.indexOf(day);

    // If the next day and the day after are sequential with the current one, we'll elide with a dash.
    // Eg  M-Th
    if (daysOfWeek.indexOf(days[0]) === num + 1 && daysOfWeek.indexOf(days[1]) === num + 2) {
      // If we're not at the start (and we didn't add one after a previous elision),
      // add a comma. Eg: M,W-F
      if (str.length > 0 && str[str.length - 1] !== ",") { str += "," }

      str += DAYS_SHORT[day] + "-";

      // Pull future days out of the array until we find one that isn't sequential (or the end of the array)
      while (daysOfWeek.indexOf(days[0]) + 1 === daysOfWeek.indexOf(days[1])) {
        days.shift();
      }
      str += DAYS_SHORT[days.shift()!]

      // If we're going to carry on after this elision, add a comma to separate it (eg: M-W,F)
      if (days.length) {
        str += ","
      }
    } else {
      // No three-day sequence, just add the current day. Eg: MWF
      str += DAYS_SHORT[day];
    }
  }

  return "Weekly: " + str;
}

export const availType = (avail: Availability, short = false) => {
  switch (avail.type) {
    case "available":
      if (short) return "Shift";
      return avail.providerTravel === "inHomeOnly" ? "In-Home Shift" : avail.providerTravel === "virtualOnly" ? "Virtual Shift" : "In-Home or Virtual Shift";
    case "unavailable":
      return "Time Off";
    case "calendar_block":
      return defaultCalendarBlockTitles[avail.blockType];
    default:
      unknownType(avail.type);
  }
}

export const availTime = (avail: Availability, timeFormatPreference?: TimeFormatPreference) => {
  if (isAllDay(avail)) {
    return "All Day";
  }

  const start = timeLabel(avail.localStartTime, { timeFormat: timeFormatPreference });
  const end = timeLabel(avail.localEndTime, { timeFormat: timeFormatPreference });
  return `${start} - ${end}`;
}

export const textDescription = (avail: Availability, timeFormatPreference?: TimeFormatPreference) => {
  let secondary = "";
  let repeats = false;
  // In our app's logic, daily rules don't recur; they span a set number of days.
  if (avail.rrule.freq === RRule.DAILY) {
    if (avail.rrule.dtstart.getTime() === avail.rrule.until.getTime()) {
      secondary = datef(avail.rrule.dtstart, "MMM d");
    } else {
      secondary = `${datef(avail.rrule.dtstart, "MMM d")} - ${datef(avail.rrule.until, "MMM d")}`;
    }
  } else {
    const recur = avail.rrule.interval === 1 ? "every week" : `every ${avail.rrule.interval} weeks`;
    const days = strJoinByWeekday(avail)
    secondary = `${days} ${recur}`
    repeats = true;
  }

  return {
    primary: `${availType(avail)}, ${availTime(avail, timeFormatPreference)}`,
    secondary,
    repeats,
  }
}

const unknownType = (type: never): never => {
  throw new Error(`Unknown type: ${(type as any)}`);
}

export const defaultAvail = (type: Availability["type"], selectedDate: Date, providerId: number, blockType?: CalendarBlockType): Availability => {
  const dtstart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 0, 0, 0, 0);

  const base = {
    id: 0,
    providerTravel: "inHomeOnly" as ProviderTravel,
    note: "",
    providerId,
    shiftCenterId: null,
    overflowShift: false,
    useShiftCenterDriveTimes: false,
    blockType: "break" as CalendarBlockType,
    blockFilters: null,
    addressStreet: null,
    addressCity: null,
    addressState: null,
    addressZip: null,
  }

  switch (type) {
    case "available": {
      const rrule: WeeklyRRule =  {
        freq: RRule.WEEKLY,
        interval: 1,
        byweekday: [daysOfWeek[dtstart.getDay()]],
        dtstart,
      };

      return {
        type: "available",
        ...base,
        ...defaultWorkday,
        rrule,
        _rrule: new RRule(rrule),
      };
    }
    case "unavailable": {
      const rrule: DailyRRule = {
        freq: RRule.DAILY,
        dtstart,
        until: dtstart,
      };

      return {
        type: "unavailable",
        ...base,
        ...allDay,
        rrule,
        _rrule: new RRule(rrule),

      }
    }
    case "calendar_block": {
      const rrule: WeeklyRRule = {
        freq: RRule.WEEKLY,
        dtstart,
        interval: 1,
        byweekday: [daysOfWeek[dtstart.getDay()]],
      };
      return {
        type: "calendar_block",
        ...base,
        ...defaultBreak,
        note: defaultCalendarBlockTitles[blockType || "break"],
        blockType: blockType || "break",
        rrule,
        blockFilters: blockType ? getDefaultFilters(blockType) : base.blockFilters,
        _rrule: new RRule(rrule),
      }
    }
    default: return unknownType(type);

  }
}

export const getAvailabilityLabel = (availability: Availability) => {
  switch (availability.type) {
    case "available": return "Availability";
    case "unavailable": return "Time Off";
    case "calendar_block": return "Recurring " + defaultCalendarBlockTitles[availability.blockType];
    default: return unknownType(availability.type);
  }
}

// Defining this as an object and then turning it into an array allows
// us to use TS to make sure the property list is exhaustive; updating
// the fields on Availability will require an update to this object as
// well, hopefully avoiding future bugs.
const filterSimilarOn: Record<keyof Availability, boolean> = {
  type: true,
  providerTravel: true,
  shiftCenterId: true,
  overflowShift: true,
  useShiftCenterDriveTimes: true,
  blockType: true,
  blockFilters: true,
  addressStreet: true,
  addressLineTwo: true,
  addressCity: true,
  addressState: true,
  addressZip: true,
  addressPlaceId: true,
  // Date fields and rrule are compared manually
  localStartTime: false,
  localEndTime: false,
  rrule: false,

  // Note is compared manually if considerDifferentNoteSimilar = false
  note: false,

  // Id and providerId are not compared
  id: false,
  providerId: false,
  _rrule: false,
};

const similarKeys = Object.keys(filterSimilarOn).filter(k => filterSimilarOn[k as keyof typeof filterSimilarOn])

export const findAvailSimilarTo = (avail: Availability, otherAvail: Availability[], considerDifferentNoteSimilar = true) => {
  const filtered = pick(avail, similarKeys);
  const today = startOfToday().getTime();

  // Availability is similar if it...
  return otherAvail
    // Isn't the one we're checking against
    .filter(other => other.id !== avail.id)
    // Has all the same user-editable fields
    .filter(other => isEqual(filtered, pick(other, similarKeys)))
    // Date fields and rrule are not handled by isEqual, need to manually compare them.
    .filter(other =>
      other.localStartTime.getTime() === avail.localStartTime.getTime()
      && other.localEndTime.getTime() === avail.localEndTime.getTime()
      // Start date only matters if it's in the future; consider all past dates equivalent to "Today".
      && Math.max(other.rrule.dtstart.getTime(), today) === Math.max(avail.rrule.dtstart.getTime(), today)
      && other.rrule.until?.getTime() === avail.rrule.until?.getTime()
      && other.rrule.freq === avail.rrule.freq
      && (other.rrule.freq !== RRule.WEEKLY || (avail.rrule.freq === RRule.WEEKLY && isEqual(other.rrule.byweekday, avail.rrule.byweekday)))
      && (considerDifferentNoteSimilar || (other.note === avail.note))
    )
}

export const offsetByTZ = <T extends {
  startTime: string, endTime: string,
}>(
  avail: T,
  fromTz: string,
  toTz: string,
): T => {
  const fromOffset = getTimezoneOffset(fromTz)
  const toOffset = getTimezoneOffset(toTz)

  const start = subMilliseconds(parse(avail.startTime, "H:mm:ss", new Date()), fromOffset - toOffset)
  const end = subMilliseconds(parse(avail.endTime, "H:mm:ss", new Date()), fromOffset - toOffset)

  return {
    ...avail,
    startTime: datef(start, "H:mm:ss"),
    endTime: datef(end, "H:mm:ss"),
  }
}
